import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, MapPin, Package, Navigation, AlertCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatAddress } from "@/lib/formatAddress";

/**
 * Phase 43.51 — Blocco "Operativo live" driver home.
 *
 * Fetcha gli ordini attivi del driver loggato e, se ce ne sono, mostra
 * la prossima azione in cima con CTA "Apri ordine".
 *
 * Predisposto multi-ordine (futuro): mostra badge "2 ordini attivi" e
 * la prossima tappa cronologica. MVP corrente: 1 ordine alla volta.
 */
const ACTIVE_STATUSES = ["accepted", "picking_up", "picked_up", "delivering"];

const STATE_MAP = {
  accepted: {
    label: "Vai al ritiro",
    sub: (o) => o.pickup_address ? `Ritiro: ${formatAddress(o.pickup_address)}` : "Pacco da ritirare",
    icon: Truck,
    accent: "#0052FF",
    bg: "bg-dapnow-blue-soft",
  },
  picking_up: {
    label: "Al ritiro · ritira il pacco",
    sub: (o) => o.pickup_address ? `Ritiro: ${formatAddress(o.pickup_address)}` : "In fase di ritiro",
    icon: Package,
    accent: "#0052FF",
    bg: "bg-dapnow-blue-soft",
  },
  picked_up: {
    label: "Pacco ritirato · in viaggio",
    sub: (o) => o.delivery_address ? `Vai a: ${formatAddress(o.delivery_address)}` : "Trasporto in corso",
    icon: Navigation,
    accent: "#10B981",
    bg: "bg-emerald-50",
  },
  delivering: {
    label: "In consegna · stai per arrivare",
    sub: (o) => o.delivery_address ? `Destinazione: ${formatAddress(o.delivery_address)}` : "Consegna in corso",
    icon: MapPin,
    accent: "#10B981",
    bg: "bg-emerald-50",
  },
};

export default function DriverActiveOpsBlock() {
  const [orders, setOrders] = useState(null); // null = loading, [] = nessuno, [..] = attivi
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/orders/mine");
        if (!mounted) return;
        setOrders((data || []).filter((o) => ACTIVE_STATUSES.includes(o.status)));
      } catch (e) {
        if (!mounted) return;
        setErr(e.message);
        setOrders([]);
      }
    };
    load();
    const tid = setInterval(load, 30_000);
    return () => { mounted = false; clearInterval(tid); };
  }, []);

  if (orders === null) {
    return (
      <section className="card-soft p-4 flex items-center gap-3 text-sm text-dapnow-muted" data-testid="driver-ops-block-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Controllo ordini attivi…
      </section>
    );
  }
  if (err || orders.length === 0) return null;

  // Ordine "in primo piano": il più avanzato nello stato operativo
  const order = pickNextOrder(orders);
  const cfg = STATE_MAP[order.status] || STATE_MAP.accepted;
  const Icon = cfg.icon;
  const multiple = orders.length > 1;

  return (
    <section
      className={`card-soft p-5 ${cfg.bg} overflow-hidden min-w-0 relative`}
      data-testid="driver-ops-block"
    >
      {/* Phase 44.31.5 — accent rail interno (no border-l-4 che sforava dal box). */}
      <span
        className="absolute top-0 left-0 h-full w-1 rounded-l-2xl"
        style={{ background: cfg.accent }}
        aria-hidden
      />
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="overline" style={{ color: cfg.accent }}>Operativo live</p>
        {multiple && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-white border border-dapnow-line px-2 py-0.5 text-[11px] font-semibold text-dapnow-ink"
            data-testid="driver-ops-multi-badge"
          >
            <AlertCircle className="h-3 w-3 text-dapnow-red" />
            {orders.length} ordini attivi
          </span>
        )}
      </div>

      <div className="flex items-start gap-4">
        <span
          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl shrink-0 shadow-sm"
          style={{ background: cfg.accent, color: "white" }}
        >
          <Icon className="h-6 w-6" />
        </span>

        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight" data-testid="driver-ops-title">
            {cfg.label}
          </h2>
          <p className="mt-1 text-sm text-dapnow-muted truncate" data-testid="driver-ops-subtitle">
            {cfg.sub(order)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to={`/driver/orders/${order.id}`}
              className="inline-flex items-center gap-1.5 rounded-xl bg-dapnow-ink text-white px-4 h-10 text-sm font-bold hover:bg-dapnow-blue transition"
              data-testid="driver-ops-open-btn"
            >
              Apri ordine #{(order.id || "").slice(0, 6).toUpperCase()}
            </Link>
            <Link
              to="/driver/active"
              className="inline-flex items-center gap-1.5 rounded-xl border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold text-dapnow-ink hover:border-dapnow-blue/40"
              data-testid="driver-ops-all-btn"
            >
              Vedi tutti gli attivi
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Priorità: delivering > picked_up > picking_up > accepted (più "avanti" prima)
const PRIORITY = { delivering: 4, picked_up: 3, picking_up: 2, accepted: 1 };
function pickNextOrder(list) {
  return [...list].sort((a, b) => {
    const pa = PRIORITY[a.status] || 0;
    const pb = PRIORITY[b.status] || 0;
    if (pb !== pa) return pb - pa;
    // a parità, l'ordine più "vecchio" (accettato prima) viene prima
    const ta = new Date(a.accepted_at || a.created_at || 0).getTime();
    const tb = new Date(b.accepted_at || b.created_at || 0).getTime();
    return ta - tb;
  })[0];
}
